import { useEffect, useState } from "react";
import store from "store";
import { getProfile } from "services/AccountServices";
import { apiGetApplicationList } from "services/ApplicationService";

// The shell (name, counsellor card) and the Home page read the same two student records. They are
// cached per signed-in session so moving between pages does not refetch them; Home passes
// `{ refresh: true }` and every subscriber (the shell included) re-renders with the fresh data.
const cache = { session: null, profile: null, requests: null };
const listeners = { profile: new Set(), requests: new Set() };

// Keyed on the redux session token: every sign-in path (email, Google, sign-up) sets it, and
// sign-out clears it, so one student's records never reach the next.
const currentSession = () => store.getState()?.auth?.session?.token || null;

export const clearStudentSession = () => {
  cache.session = null;
  cache.profile = null;
  cache.requests = null;
};

const cached = (key, loader, refresh) => {
  const session = currentSession();
  if (cache.session !== session) {
    clearStudentSession();
    cache.session = session;
  }
  if (!cache[key] || refresh) {
    // Subscribers get fresh values pushed to them; a failed refresh keeps what they already show.
    const request = loader().then(
      (value) => {
        listeners[key].forEach((listener) => listener(value));
        return value;
      },
      (error) => {
        if (cache[key] === request) {
          cache[key] = null;
        }
        throw error;
      }
    );
    cache[key] = request;
  }
  return cache[key];
};

const QUIET = { suppressErrorToast: true };

export const loadProfile = ({ refresh = false } = {}) =>
  cached("profile", () => getProfile({}, QUIET).then((res) => res?.data || {}), refresh);

export const loadApplicationRequests = ({ refresh = false } = {}) =>
  cached(
    "requests",
    () => apiGetApplicationList({}, QUIET).then((res) => (Array.isArray(res?.data) ? res.data : [])),
    refresh
  );

// Older student APIs only attach the assigned counsellor to application requests.
export const counsellorFrom = (requests = []) => requests.find((r) => r?.counsellor?.counsellorId)?.counsellor || null;

// The profile carries `counsellor` (null when nobody is assigned) on newer student APIs, which also
// covers students who haven't applied yet. Without that field, or if the profile fails, fall back
// to the application requests.
export const loadAssignedCounsellor = () =>
  loadProfile()
    .catch(() => ({}))
    .then((profile) =>
      Object.prototype.hasOwnProperty.call(profile || {}, "counsellor")
        ? profile.counsellor
        : loadApplicationRequests().then(counsellorFrom)
    );

// `keys` are the cached records the value derives from; a fresh copy of any of them re-reads it
// from the cache.
const useLoaded = (keys, loader) => {
  const [state, setState] = useState({ value: null, loading: true, error: false });
  useEffect(() => {
    let mounted = true;
    const show = (value) => mounted && setState({ value, loading: false, error: false });
    const refresh = () => loader().then(show).catch(() => {});
    loader()
      .then(show)
      .catch(() => mounted && setState((prev) => ({ ...prev, loading: false, error: true })));
    keys.forEach((key) => listeners[key].add(refresh));
    return () => {
      mounted = false;
      keys.forEach((key) => listeners[key].delete(refresh));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
};

export const useStudentProfile = () => useLoaded(["profile"], loadProfile);

export const useAssignedCounsellor = () => useLoaded(["profile", "requests"], loadAssignedCounsellor);
